// markup/MarkupLayer.jsx — DropPin plan-markup render + interaction surface.
// ---------------------------------------------------------------------------
// One SVG overlay that shares the plan viewer's transform (it renders its own
// wrapper with the SAME translate()/scale() as the image div, sized to the
// drawing's natural pixels), so every annotation registers pixel-exact with the
// plan under pan/zoom — the same coordinate trick DropPin's pins already use.
//
// Objects are stored NORMALISED (0..1) in the model; here they're drawn in
// natural-PIXEL space (x*W, y*H). Every on-screen-constant size (stroke, handle,
// font, arrowhead, dashes) is divided by `scale` so it stays a fixed screen
// size at any zoom. Pointer input is converted client -> normalised via the
// screen's own proven transform (clientToImagePx), so markup lines up with pins.
//
// Editing (v0.80): the select tool now RESIZES rectangles + the text box (corner
// / edge handles), RESHAPES polygons / polylines / lines / measures (per-vertex
// handles), and MARQUEE-selects (rubber-band). Holding Shift ortho-constrains a
// segment to 0/45/90°, and new / edited vertices snap to nearby existing
// vertices (+ an optional light grid). All the geometry is the shared core's,
// via ../edit + ../measure + ../model — this file only draws and routes gestures.
// ---------------------------------------------------------------------------

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  createObject, defaultStyle, rectXYWH, boundsOf, toPixels, centroidPx,
  withMarkType, markTypesFor, retypePlan, retypeTitle, TYPED_FILL_OPACITY,
} from './model.mjs';
import {
  LEGEND_TITLE, legendLayout, legendBoxNorm, legendFontSizeForDrag, legendRowsFrom, readableInk,
} from './legend.mjs';
import { legendTotals } from './takeoff.mjs';
import MarkTypePicker from './MarkTypePicker.jsx';
import { measureLength, measureArea } from './measure.mjs';
import {
  RECT_HANDLES, rectHandleCursor, rectHandlePoint, applyRectHandle, bboxToRectPoints,
  moveVertex, textBoxNorm, fontSizeForDrag, rectFromCorners, marqueeSelect,
  collectSnapPoints, resolvePoint, nodeAt,
} from './edit.mjs';

const HANDLE = 7;      // rect resize handle half-size, screen px
const VHANDLE = 5;     // vertex handle radius, screen px
const TOUCH = 14;      // invisible touch target radius/half, screen px
const HIT_TOL = 9;     // click hit tolerance, screen px
const NODE_TOL = 10;   // a press this close to a corner takes the node, screen px
const CLOSE_TOL = 12;  // "click near first vertex to close", screen px
const SNAP_TOL = 10;   // vertex-snap threshold, screen px
const GRID_PX = 24;    // light grid step, NATURAL px (fixed to the image)

const LEGEND_SCREEN_FONT = 13; // a new legend's type size on screen, at the zoom it is stamped at

function cursorForTool(tool) {
  if (tool === 'select') return 'default';
  if (tool === 'pan') return 'grab';
  if (tool === 'text') return 'text';
  return 'crosshair';
}

export default function MarkupLayer({
  m, naturalW, naturalH, scale, tx, ty, clientToImagePx,
  // v0.139.0 — pan WHILE a command is running. The stage owns the transform, so it
  // hands down how to start a pan drag; middle-button or held-Space routes
  // straight to it without disturbing the half-drawn shape.
  onPanStart = null,
  // v0.139.0 — read-only render. Markup no longer disappears when you leave markup
  // mode: the plan keeps showing the marks, it just stops taking input.
  readOnly = false,
  // 2026-09-15 — the tenant's mark types: what a new legend lists, and the
  // current name each legend row shows (a type renamed in Settings reads right).
  markTypes = null,
  // 2026-09-15 — a zoom applied by an ANCESTOR (the shared report puts this
  // layer inside its own zoomed plan): labels, strokes and handles counter-scale
  // by it as well, so they stay a constant size on screen.
  outerScale = 1,
}) {
  const W = naturalW || 1;
  const H = naturalH || 1;
  const inv = 1 / ((scale || 1) * (outerScale || 1));

  const [draft, setDraft] = useState(null);      // in-progress shape
  const [hoverPt, setHoverPt] = useState(null);   // normalised cursor (multi-point rubber-band)
  const [editing, setEditing] = useState(null);   // { id|null, anchor:{x,y}norm, value }
  const [marquee, setMarquee] = useState(null);   // { a:{x,y}, b:{x,y} } rubber-band select
  const [dragging, setDragging] = useState(false); // a pointer drag is live
  const [typePicker, setTypePicker] = useState(null); // { id, ids, x, y } — the type menu: the mark pressed, the marks it may change
  const lastTapRef = useRef(null);                // { id, at } — double-tap on a touch screen
  const [activeNode, setActiveNode] = useState(null); // { id, index } — the node last taken
  const [hoverNode, setHoverNode] = useState(null);   // { id, index } — the node a press would take
  const draftRef = useRef(null);
  const editingRef = useRef(null);
  const dragAbortRef = useRef(null);              // abort the live drag WITHOUT committing
  const spaceRef = useRef(false);                 // Space held -> pan, not draw
  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => { editingRef.current = editing; }, [editing]);
  // The taken node belongs to the one selected shape; select anything else and
  // it lets go.
  const selKey = m.selectedIds.map(String).join(',');
  useEffect(() => {
    setActiveNode((cur) => (cur && selKey !== cur.id ? null : cur));
  }, [selKey]);

  // ---- Abortable drag plumbing --------------------------------------------
  // Every window-listener drag goes through this so Escape (or the toolbar's
  // Cancel) can tear it down WITHOUT running its commit step — previously the
  // draft was cleared but the pending pointerup still added the object, so
  // cancelling a line or a rectangle silently drew it anyway.
  const bindDrag = useCallback((onMove, onUp, onAbort) => {
    const onWinMove = (ev) => onMove(ev);
    let live = true;
    const stop = () => {
      if (!live) return;
      live = false;
      window.removeEventListener('pointermove', onWinMove);
      window.removeEventListener('pointerup', onWinUp);
      window.removeEventListener('pointercancel', onWinUp);
      dragAbortRef.current = null;
      setDragging(false);
    };
    function onWinUp(ev) { stop(); onUp(ev); }
    window.addEventListener('pointermove', onWinMove);
    window.addEventListener('pointerup', onWinUp);
    window.addEventListener('pointercancel', onWinUp);
    dragAbortRef.current = () => { stop(); if (onAbort) onAbort(); };
    setDragging(true);
    return stop;
  }, []);

  const toNorm = useCallback((cx, cy) => {
    const p = clientToImagePx(cx, cy);
    if (!p) return null;
    return { x: p.x / W, y: p.y / H };
  }, [clientToImagePx, W, H]);

  // 2026-09-15 — Brett: "i used the shortcut buttons for new tiles and full
  // regrout and they both come out blue." A measure took no style from here, so
  // the model's blue measure default beat the type's colour every time. With a
  // type armed, its colour IS the mark's colour, line and fill: the colour is
  // how anyone reading the plan tells new tiles from a regrout.
  const styleForNew = (type) => {
    const typeColor = m.markType && m.markType.color;
    if (typeColor) {
      const fills = type === 'measure' || type === 'polygon' || type === 'rect';
      return {
        stroke: typeColor,
        ...(fills ? { fill: typeColor, fillOpacity: TYPED_FILL_OPACITY } : {}),
        ...(type === 'measure' ? {} : { strokeWidth: m.drawStyle.strokeWidth }),
      };
    }
    return type === 'measure' ? {} : { stroke: m.drawStyle.stroke, strokeWidth: m.drawStyle.strokeWidth };
  };

  const pxDist = (a, b) => Math.hypot((b.x - a.x) * W, (b.y - a.y) * H);

  // ---- Legend names + totals (2026-09-15) ----------------------------------
  const typeNames = useMemo(() => {
    const out = {};
    for (const t of (markTypes || [])) if (t && t.key) out[t.key] = t.label;
    return out;
  }, [markTypes]);
  const legendTotalsByKey = useMemo(() => {
    const keys = new Set();
    for (const o of m.objects) if (o.type === 'legend') for (const r of (o.rows || [])) keys.add(r.key);
    return keys.size ? legendTotals(m.objects, W, H, m.calibration, m.unit, [...keys]) : {};
  }, [m.objects, W, H, m.calibration, m.unit]);

  // ---- Snapping helpers ----------------------------------------------------
  // Options for resolvePoint given the live pointer event + optional ortho
  // anchor + which object (if any) to exclude from vertex-snap targets.
  const snapOpts = (ev, anchor, excludeId) => ({
    anchor: anchor || null,
    ortho: !!(ev && ev.shiftKey) && !!anchor,
    snapPts: collectSnapPoints(m.objects, excludeId),
    tolPx: SNAP_TOL * inv,
    grid: !!m.snapGrid,
    gridPx: GRID_PX,
    W, H,
  });
  // A NEW starting point snaps to existing vertices + grid (no ortho anchor).
  const snapStart = (norm) => resolvePoint(norm, snapOpts(null, null, null));

  // ---- Finish / cancel multi-point draft ----------------------------------
  const finishMulti = useCallback(() => {
    const d = draftRef.current;
    if (!d || d._mode !== 'multi') return;
    const closed = d.type === 'polygon' || (d.type === 'measure' && d.measureKind === 'area');
    const minPts = closed ? 3 : 2;
    if (d.points.length >= minPts) {
      m.addObject(createObject(d.type, {
        points: d.points,
        measureKind: d.measureKind || undefined,
        style: styleForNew(d.type),
        // 2026-08-14 — what KIND of thing this is, from the tenant's catalogue.
        // Carried on the mark itself so totals can group by it later; the model
        // validates and persists it (model.mjs sanitizeInObject).
        typeKey: (m.markType && m.markType.key) || undefined,
      }));
    }
    setDraft(null);
    setHoverPt(null);
  }, [m]);

  // ---- Cancel / finish the command in progress -----------------------------
  // Cancel tears down a live drag without committing it, drops the half-drawn
  // shape and any rubber-band, and leaves the TOOL armed — Escape in a CAD app
  // backs out of the command, it doesn't put the tools away.
  const cancelCommand = useCallback(() => {
    const abort = dragAbortRef.current;
    dragAbortRef.current = null;
    if (abort) abort();
    setDraft(null);
    setHoverPt(null);
    setMarquee(null);
    setEditing(null);
  }, []);

  // Enter finishes a multi draft; Escape cancels whatever is running.
  useEffect(() => {
    if (readOnly) return undefined;
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'Enter') {
        if (draftRef.current && draftRef.current._mode === 'multi') { finishMulti(); e.preventDefault(); }
      } else if (e.key === ' ' || e.code === 'Space') {
        spaceRef.current = true;                 // held Space = pan, even mid-command
      }
    };
    const onKeyUp = (e) => { if (e.key === ' ' || e.code === 'Space') spaceRef.current = false; };
    const onBlur = () => { spaceRef.current = false; };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [readOnly, finishMulti]);

  // Publish cancel / finish to the controller so the TOOLBAR can offer them.
  // Escape and Enter don't exist on an iPad, so the buttons are the only way a
  // touch user backs out of a half-drawn area.
  const { registerCommand } = m;
  const running = !!draft || !!marquee || dragging || !!editing;
  useEffect(() => {
    if (readOnly || !registerCommand) return undefined;
    registerCommand(running ? { cancel: cancelCommand, finish: finishMulti } : null);
    return () => registerCommand(null);
  }, [readOnly, running, registerCommand, cancelCommand, finishMulti]);

  // ---- Text editor commit / cancel ----------------------------------------
  const commitText = useCallback(() => {
    const ed = editingRef.current;
    setEditing(null);
    if (!ed) return;
    const v = ed.value || '';
    if (ed.id == null) {
      if (v.trim()) {
        m.addObject(createObject('text', {
          points: [ed.anchor], text: v,
          style: { stroke: m.drawStyle.stroke, fill: m.drawStyle.stroke },
        }));
      }
    } else if (v.trim()) {
      m.updateText(ed.id, v);
    } else {
      m.deleteObjects([ed.id]);
    }
  }, [m]);
  const cancelText = useCallback(() => setEditing(null), []);

  // ---- Two-point drag tools (line / arrow / rect / calibrate) --------------
  const startTwoPoint = (tool, norm) => {
    const type = tool === 'calibrate' ? 'line' : tool;
    const isLinear = (type === 'line' || type === 'arrow'); // ortho applies to these
    setDraft({ _mode: 'two', type, calibrate: tool === 'calibrate', points: [norm, norm] });
    const resolveEnd = (ev) => {
      const n = toNorm(ev.clientX, ev.clientY);
      if (!n) return null;
      return resolvePoint(n, snapOpts(ev, isLinear ? norm : null, null));
    };
    bindDrag(
      (ev) => {
        const n = resolveEnd(ev);
        setDraft((cur) => (cur ? { ...cur, points: [cur.points[0], n || cur.points[1]] } : cur));
      },
      (ev) => {
        const n = resolveEnd(ev) || norm;
        setDraft(null);
        if (pxDist(norm, n) < 3) return; // ignore accidental taps
        if (tool === 'calibrate') { m.setCalibrationLine(norm, n); return; }
        m.addObject(createObject(type, {
          points: [norm, n], style: styleForNew(type),
          typeKey: (m.markType && m.markType.key) || undefined,
        }));
      },
      () => setDraft(null),
    );
  };

  // ---- Freehand (never snapped / constrained) ------------------------------
  const startFreehand = (norm) => {
    setDraft({ _mode: 'free', type: 'freehand', points: [norm] });
    bindDrag(
      (ev) => {
        const n = toNorm(ev.clientX, ev.clientY);
        if (n) setDraft((cur) => (cur ? { ...cur, points: [...cur.points, n] } : cur));
      },
      () => {
        const d = draftRef.current;
        setDraft(null);
        if (d && d.points.length >= 2) {
          m.addObject(createObject('freehand', {
            points: d.points, style: styleForNew('freehand'),
            typeKey: (m.markType && m.markType.key) || undefined,
          }));
        }
      },
      () => setDraft(null),
    );
  };

  // ---- Multi-point click tools (polygon / polyline / measure) --------------
  const addMultiPoint = (norm, ev) => {
    setDraft((cur) => {
      if (!cur || cur._mode !== 'multi') {
        const isMeasure = m.tool === 'measure';
        return {
          _mode: 'multi',
          type: isMeasure ? 'measure' : m.tool,
          measureKind: isMeasure ? m.measureKind : null,
          points: [snapStart(norm)],
        };
      }
      const anchor = cur.points[cur.points.length - 1];
      const p = resolvePoint(norm, snapOpts(ev, anchor, null));
      const closed = cur.type === 'polygon' || (cur.type === 'measure' && cur.measureKind === 'area');
      if (closed && cur.points.length >= 3 && pxDist(cur.points[0], p) < CLOSE_TOL * inv) {
        // Click near the first vertex closes the shape.
        setTimeout(finishMulti, 0);
        return cur;
      }
      return { ...cur, points: [...cur.points, p] };
    });
  };

  // ---- Legend stamp (2026-09-15) --------------------------------------------
  // Brett: "make me a legend button so i can stamp the drawing with those
  // colours." One tap stamps it with its top-left corner where you tapped, at a
  // size that reads at the zoom you are at. It arrives selected, so it can be
  // dragged into a clear corner or sized from its handle straight away.
  const placeLegend = (norm) => {
    const rows = legendRowsFrom(markTypes);
    if (rows.length) {
      m.addObject(createObject('legend', {
        points: [norm], rows, style: { fontSize: LEGEND_SCREEN_FONT * inv },
      }));
    }
    m.setTool('select');
  };

  // ---- Change a mark's type (2026-09-15) ------------------------------------
  // Brett: "double click an area and assign it to one of the shortcut measures.
  // and change it from one to another". A double-click (a double-tap on a
  // tablet) on a mark opens the types that fit it; picking one re-colours it and
  // re-totals it as that type, in one undo step. A text note opens its editor,
  // as a double-click always did.
  // 2026-10-21 — Brett: "we should be able to select multiple areas and change
  // its type at once." Pressed on a mark that is part of a selection of several,
  // the menu changes every selected mark of its family (retypePlan) — still one
  // undo step.
  const typePlanFor = (pick) => {
    if (!pick) return null;
    const live = (ids) => m.objects.filter((o) => ids.includes(String(o.id)));
    const anchor = pick.id != null ? m.objects.find((o) => String(o.id) === String(pick.id)) : null;
    if (pick.id != null && !anchor) return null;
    return retypePlan(live(pick.ids), anchor, markTypes);
  };
  const openMarkEditor = (obj, clientX, clientY) => {
    if (!obj) return false;
    if (obj.type === 'text') { setEditing({ id: obj.id, anchor: obj.points[0], value: obj.text || '' }); return true; }
    if (!markTypesFor(obj, markTypes).length) return false;
    const sel = m.selectedIds.map(String);
    const ids = sel.length > 1 && sel.includes(String(obj.id)) ? sel : [String(obj.id)];
    setTypePicker({ id: String(obj.id), ids, x: clientX, y: clientY });
    return true;
  };
  const pickMarkType = (t) => {
    const plan = typePlanFor(typePicker);
    setTypePicker(null);
    if (plan && plan.targets.length) m.updateObjects(plan.targets.map((o) => withMarkType(o, t)));
  };

  // ---- Marquee (rubber-band) multi-select ----------------------------------
  const startMarquee = (e, norm) => {
    const additive = e.shiftKey;
    if (!additive) m.clearSelection();
    setMarquee({ a: norm, b: norm });
    bindDrag(
      (ev) => {
        const n = toNorm(ev.clientX, ev.clientY);
        if (n) setMarquee({ a: norm, b: n });
      },
      (ev) => {
        const n = toNorm(ev.clientX, ev.clientY) || norm;
        setMarquee(null);
        if (pxDist(norm, n) < 3) return; // a click on empty canvas — selection already cleared
        const ids = marqueeSelect(m.objects, rectFromCorners(norm, n), W, H);
        if (ids.length) m.select(ids, additive);
      },
      () => setMarquee(null),
    );
  };

  // ---- Select + move (body drag) ------------------------------------------
  const startSelectOrMove = (e, norm) => {
    const pPx = { x: norm.x * W, y: norm.y * H };
    // 2026-09-15 — Brett: "need to be able to select a node and relocate it". A
    // press on a corner takes THAT NODE, selected shape or not, instead of
    // dragging the whole shape by it (which a press on the corner dot of an
    // unselected area did). Shift still adds to the selection.
    if (!e.shiftKey) {
      const node = nodeAt(m.objects, pPx, NODE_TOL * inv, W, H, m.selectedIds);
      const nodeObj = node && m.objects.find((o) => String(o.id) === String(node.id));
      if (nodeObj) {
        m.select([nodeObj.id], false);
        return startHandleDrag(e, nodeObj, { type: 'vertex', index: node.index });
      }
    }
    setActiveNode(null);
    const hitId = m.hitTopmost(pPx, HIT_TOL * inv, W, H);
    if (!hitId) return startMarquee(e, norm); // empty canvas -> rubber-band
    // A second tap on the same mark within a moment is a double-tap. Touch sends
    // no dblclick here: this surface takes every touch itself (touch-action none).
    const now = Date.now();
    const lastTap = lastTapRef.current;
    lastTapRef.current = { id: String(hitId), at: now };
    if (e.pointerType && e.pointerType !== 'mouse' && lastTap && lastTap.id === String(hitId) && now - lastTap.at < 400) {
      lastTapRef.current = null;
      if (openMarkEditor(m.objects.find((o) => String(o.id) === String(hitId)), e.clientX, e.clientY)) return;
    }
    if (e.shiftKey) m.select([hitId], true);
    else if (!m.selectedIds.map(String).includes(String(hitId))) m.select([hitId], false);

    m.beginMove();
    const start = norm;
    bindDrag(
      (ev) => {
        const n = toNorm(ev.clientX, ev.clientY);
        if (n) m.previewMove(n.x - start.x, n.y - start.y);
      },
      (ev) => {
        const n = toNorm(ev.clientX, ev.clientY) || start;
        m.commitMove(n.x - start.x, n.y - start.y);
      },
      // Cancelled mid-move: put it back exactly where it was (a 0,0 commit
      // restores the snapshot and records no undo step).
      () => m.commitMove(0, 0),
    );
  };

  // ---- Resize / reshape via a handle drag ----------------------------------
  // `handle` is { type:'rect', id } | { type:'text' } | { type:'vertex', index }.
  const startHandleDrag = (e, obj, handle) => {
    if (e.button === 2) return;
    e.stopPropagation();
    e.preventDefault();
    setActiveNode(handle.type === 'vertex' ? { id: String(obj.id), index: handle.index } : null);
    const downAt = { x: e.clientX, y: e.clientY };
    let moved = false;
    // A node keeps its offset from the pointer, so taking it a few pixels
    // off-centre does not make it jump to where the pointer is.
    const downNorm = toNorm(e.clientX, e.clientY);
    const grab = (handle.type === 'vertex' && downNorm && obj.points[handle.index])
      ? { dx: obj.points[handle.index].x - downNorm.x, dy: obj.points[handle.index].y - downNorm.y }
      : { dx: 0, dy: 0 };
    m.beginEdit();
    const base = obj;                       // snapshot at drag start
    const baseStyle = obj.style || {};
    const snapPts = collectSnapPoints(m.objects, obj.id); // static targets (self excluded)

    // Ortho anchor for a vertex reshape = its neighbour (prev, else next).
    const vAnchor = (o, i) => (i > 0 ? o.points[i - 1] : (o.points.length > 1 ? o.points[i + 1] : null));

    const patchFor = (ev) => {
      const raw = toNorm(ev.clientX, ev.clientY);
      if (!raw) return null;
      if (handle.type === 'rect') {
        const n = resolvePoint(raw, { snapPts, tolPx: SNAP_TOL * inv, grid: !!m.snapGrid, gridPx: GRID_PX, W, H });
        const nb = applyRectHandle(boundsOf(base, 0), handle.id, n.x, n.y);
        return { points: bboxToRectPoints(nb) };
      }
      if (handle.type === 'legend') {
        return { style: { ...baseStyle, fontSize: legendFontSizeForDrag(base, raw, W, H) } };
      }
      if (handle.type === 'text') {
        const fs = fontSizeForDrag(base.points[0], raw, H);
        return { style: { ...baseStyle, fontSize: fs } };
      }
      // vertex reshape
      const anchor = vAnchor(base, handle.index);
      const n = resolvePoint({ x: raw.x + grab.dx, y: raw.y + grab.dy }, {
        anchor, ortho: !!ev.shiftKey && !!anchor, snapPts,
        tolPx: SNAP_TOL * inv, grid: !!m.snapGrid, gridPx: GRID_PX, W, H,
      });
      return { points: moveVertex(base.points, handle.index, n.x, n.y) };
    };

    bindDrag(
      (ev) => {
        // A press that has not travelled is a pick, not a drag: nothing moves
        // until the pointer does, so selecting a node never nudges it.
        if (!moved && Math.hypot(ev.clientX - downAt.x, ev.clientY - downAt.y) < 3) return;
        moved = true;
        const p = patchFor(ev);
        if (p) m.previewEdit(obj.id, p);
      },
      (ev) => {
        const p = moved ? patchFor(ev) : null;
        m.commitEdit(obj.id, p || {}, !!p);
      },
      // Cancelled mid-reshape: restore the snapshot, no undo step.
      () => m.commitEdit(obj.id, {}, false),
    );
  };

  // ---- Pointer routing -----------------------------------------------------
  const onPointerDown = (e) => {
    if (readOnly) return;
    if (e.button === 2) return;         // right-click -> context (ignored)
    // PAN, EVEN MID-COMMAND. Middle-button or held Space starts a stage pan and
    // leaves the half-drawn shape exactly as it is — you can walk the plan to
    // find the far corner of an area without losing the points you've placed.
    if (onPanStart && (e.button === 1 || spaceRef.current)) {
      e.stopPropagation();              // the stage would otherwise also drop a pin
      e.preventDefault();
      onPanStart(e.clientX, e.clientY);
      return;
    }
    e.stopPropagation();                 // keep the stage from panning / dropping a pin
    if (editingRef.current) { commitText(); return; }
    const norm = toNorm(e.clientX, e.clientY);
    if (!norm) return;
    const tool = m.tool;
    if (tool === 'select') return startSelectOrMove(e, norm);
    if (tool === 'legend') return placeLegend(norm);
    if (tool === 'text') { setEditing({ id: null, anchor: snapStart(norm), value: '' }); return; }
    if (tool === 'freehand') return startFreehand(norm);
    if (tool === 'line' || tool === 'arrow' || tool === 'rect' || tool === 'calibrate') {
      return startTwoPoint(tool, snapStart(norm));
    }
    if (tool === 'polygon' || tool === 'polyline' || tool === 'measure') return addMultiPoint(norm, e);
  };

  const onSvgMove = (e) => {
    // Select tool: show which node a press would take — a ring and the grab
    // cursor — on shapes that are not selected as well.
    if (m.tool === 'select' && !dragging) {
      const n = toNorm(e.clientX, e.clientY);
      const node = n && nodeAt(m.objects, { x: n.x * W, y: n.y * H }, NODE_TOL * inv, W, H, m.selectedIds);
      const next = node ? `${node.id}:${node.index}` : null;
      const cur = hoverNode ? `${hoverNode.id}:${hoverNode.index}` : null;
      if (next !== cur) setHoverNode(node ? { id: String(node.id), index: node.index } : null);
    }
    const d = draftRef.current;
    if (d && d._mode === 'multi') {
      const n = toNorm(e.clientX, e.clientY);
      if (n) {
        const anchor = d.points[d.points.length - 1];
        setHoverPt(resolvePoint(n, snapOpts(e, anchor, null)));
      }
    }
  };

  const onDoubleClick = (e) => {
    if (readOnly) return;
    e.stopPropagation();
    if (draftRef.current && draftRef.current._mode === 'multi') { finishMulti(); return; }
    if (m.tool !== 'select') return;
    const norm = toNorm(e.clientX, e.clientY);
    if (!norm) return;
    const id = m.hitTopmost({ x: norm.x * W, y: norm.y * H }, HIT_TOL * inv, W, H);
    const obj = id && m.objects.find((o) => String(o.id) === String(id));
    openMarkEditor(obj, e.clientX, e.clientY);
  };

  // =========================================================================
  // Rendering helpers
  const selSet = new Set(m.selectedIds.map(String));

  const arrowHead = (a, b, color, sw) => {
    const ang = Math.atan2(b.y - a.y, b.x - a.x);
    const size = (12 * inv) + sw * 1.5;
    const a1 = ang + Math.PI - 0.42, a2 = ang + Math.PI + 0.42;
    const p1 = { x: b.x + size * Math.cos(a1), y: b.y + size * Math.sin(a1) };
    const p2 = { x: b.x + size * Math.cos(a2), y: b.y + size * Math.sin(a2) };
    return <polygon points={`${b.x},${b.y} ${p1.x},${p1.y} ${p2.x},${p2.y}`} fill={color} stroke={color} strokeWidth={sw * 0.5} strokeLinejoin="round" />;
  };

  const vertexDots = (pts, color) => pts.map((p, i) => (
    <circle key={`v${i}`} cx={p.x} cy={p.y} r={3 * inv} fill="#ffffff" stroke={color} strokeWidth={1.4 * inv} />
  ));

  const measureLabelFor = (o, pts) => {
    if (!pts.length || m.hideFigures) return null; // v0.304.0 — the client report shows no figures
    const isArea = o.measureKind === 'area';
    const res = isArea
      ? measureArea(o.points, W, H, m.calibration, m.unit)
      : measureLength(o.points, W, H, m.calibration, m.unit);
    const lines = isArea ? [res.areaLabel, `P ${res.perimLabel}`] : [res.label];
    const anchor = isArea ? centroidPx(o, W, H) : pts[pts.length - 1];
    // The chip wears the mark's own colour, so a figure is read against its area.
    return <MLabel x={anchor.x} y={anchor.y} lines={lines} color={(o.style && o.style.stroke) || '#2563eb'} inv={inv} />;
  };

  const renderShape = (o, key, ghost) => {
    const pts = toPixels(o.points, W, H);
    const stroke = o.style.stroke || '#e8650a';
    const sw = (o.style.strokeWidth != null ? o.style.strokeWidth : 2) * inv;
    const fill = o.style.fill || 'none';
    const fillOpacity = o.style.fillOpacity != null ? o.style.fillOpacity : 0;
    const dash = ghost ? { strokeDasharray: `${6 * inv} ${5 * inv}` } : null;
    const common = { stroke, strokeWidth: sw, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round', ...dash };

    switch (o.type) {
      case 'line':
        return <line key={key} x1={pts[0].x} y1={pts[0].y} x2={pts[1].x} y2={pts[1].y} {...common} />;
      case 'arrow':
        return <g key={key}>
          <line x1={pts[0].x} y1={pts[0].y} x2={pts[1].x} y2={pts[1].y} {...common} />
          {arrowHead(pts[0], pts[1], stroke, sw)}
        </g>;
      case 'rect': {
        const r = rectXYWH(o);
        return <rect key={key} x={r.x * W} y={r.y * H} width={r.w * W} height={r.h * H}
          {...common} fill={fill} fillOpacity={fillOpacity} />;
      }
      case 'polygon':
        return <polygon key={key} points={pts.map((p) => `${p.x},${p.y}`).join(' ')}
          {...common} fill={fill} fillOpacity={fillOpacity} />;
      case 'polyline':
      case 'freehand':
        return <polyline key={key} points={pts.map((p) => `${p.x},${p.y}`).join(' ')} {...common} />;
      case 'text': {
        const fs = (o.style.fontSize || 15) * inv;
        return <text key={key} x={pts[0].x} y={pts[0].y} fontSize={fs}
          fill={o.style.fill || stroke} stroke="#ffffff" strokeWidth={fs * 0.14}
          paintOrder="stroke" style={{ fontFamily: 'var(--font-ui)', fontWeight: 600 }}
          dominantBaseline="hanging">{o.text || ''}</text>;
      }
      case 'measure': {
        const isArea = o.measureKind === 'area';
        const el = isArea
          ? <polygon points={pts.map((p) => `${p.x},${p.y}`).join(' ')} {...common} fill={fill} fillOpacity={fillOpacity} />
          : <polyline points={pts.map((p) => `${p.x},${p.y}`).join(' ')} {...common} />;
        return <g key={key}>{el}{vertexDots(pts, stroke)}{measureLabelFor(o, pts)}</g>;
      }
      case 'legend': {
        // A stamp, not a note: sized in the drawing's own pixels so it zooms
        // with the plan. Rows are the types it was stamped with; each total is
        // what that type adds up to on this drawing right now.
        const L = legendLayout(o);
        const a = pts[0];
        const font = { fontFamily: 'var(--font-ui)' };
        return (
          <g key={key} transform={`translate(${a.x} ${a.y})`}>
            <rect x={0} y={0} width={L.w} height={L.h} rx={L.fs * 0.3}
              fill={o.style.fill || '#ffffff'} fillOpacity={0.96}
              stroke={stroke} strokeWidth={L.fs * 0.08} />
            <text x={L.pad} y={L.pad + L.titleH / 2} dominantBaseline="central"
              fontSize={L.fs * 0.9} fill={stroke}
              style={{ ...font, fontWeight: 800, letterSpacing: L.fs * 0.1 }}>{LEGEND_TITLE}</text>
            {L.rows.map((r) => {
              const cy = r.top + L.rowH / 2;
              return (
                <g key={r.key}>
                  <rect x={L.pad} y={cy - L.swatch / 2} width={L.swatch} height={L.swatch} rx={L.fs * 0.15}
                    fill={r.color} fillOpacity={0.35} stroke={r.color} strokeWidth={L.fs * 0.14} />
                  <text x={L.labelX} y={cy} dominantBaseline="central" fontSize={L.fs} fill="#0f172a"
                    style={{ ...font, fontWeight: 600 }}>{typeNames[r.key] || r.label}</text>
                  {L.totals && (
                    <text x={L.totalsX} y={cy} dominantBaseline="central" textAnchor="end"
                      fontSize={L.fs * 0.9} fill="#334155"
                      style={{ ...font, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {legendTotalsByKey[r.key] || '—'}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        );
      }
      default:
        return null;
    }
  };

  // Selection outline (dashed bbox). Text uses its measured box; others boundsOf.
  const renderSelectionOutline = (o, key) => {
    const b = o.type === 'text' ? textBoxNorm(o, W, H) : o.type === 'legend' ? legendBoxNorm(o, W, H) : boundsOf(o, 0);
    if (!b) return null;
    const x = b.minX * W, y = b.minY * H, w = (b.maxX - b.minX) * W, h = (b.maxY - b.minY) * H;
    const pad = 4 * inv;
    return <rect key={key} pointerEvents="none"
      x={x - pad} y={y - pad} width={w + pad * 2} height={h + pad * 2}
      fill="none" stroke="#2563eb" strokeWidth={1.4 * inv} strokeDasharray={`${5 * inv} ${4 * inv}`} />;
  };

  // Interactive resize / reshape handles for the SINGLE selected object.
  const rectHandle = (o, id) => {
    const p = rectHandlePoint(boundsOf(o, 0), id);
    const cx = p.x * W, cy = p.y * H;
    return (
      <g key={`rh-${id}`} style={{ cursor: rectHandleCursor(id) }}
        onPointerDown={(e) => startHandleDrag(e, o, { type: 'rect', id })}>
        <rect x={cx - TOUCH * inv} y={cy - TOUCH * inv} width={TOUCH * 2 * inv} height={TOUCH * 2 * inv} fill="transparent" />
        <rect x={cx - HANDLE * inv} y={cy - HANDLE * inv} width={HANDLE * 2 * inv} height={HANDLE * 2 * inv}
          fill="#ffffff" stroke="#2563eb" strokeWidth={1.4 * inv} rx={2 * inv} pointerEvents="none" />
      </g>
    );
  };

  const vertexHandle = (o, i) => {
    const p = o.points[i];
    const on = !!activeNode && activeNode.id === String(o.id) && activeNode.index === i;
    const cx = p.x * W, cy = p.y * H;
    return (
      <g key={`vh-${i}`} style={{ cursor: 'grab' }}
        onPointerDown={(e) => startHandleDrag(e, o, { type: 'vertex', index: i })}>
        <circle cx={cx} cy={cy} r={TOUCH * inv} fill="transparent" />
        <circle cx={cx} cy={cy} r={(on ? VHANDLE + 1.5 : VHANDLE) * inv} fill={on ? '#2563eb' : '#ffffff'}
          stroke={on ? '#ffffff' : '#2563eb'} strokeWidth={(on ? 2 : 1.6) * inv} pointerEvents="none" />
      </g>
    );
  };

  const renderHandles = (o) => {
    if (o.type === 'freehand') return null;           // move-only (too many points)
    if (o.type === 'rect') return RECT_HANDLES.map((id) => rectHandle(o, id));
    if (o.type === 'legend') {
      // One corner: drag it out to make the stamp bigger, in to make it smaller.
      const b = legendBoxNorm(o, W, H);
      const cx = b.maxX * W, cy = b.maxY * H;
      return (
        <g style={{ cursor: 'nwse-resize' }} onPointerDown={(e) => startHandleDrag(e, o, { type: 'legend' })}>
          <rect x={cx - TOUCH * inv} y={cy - TOUCH * inv} width={TOUCH * 2 * inv} height={TOUCH * 2 * inv} fill="transparent" />
          <rect x={cx - HANDLE * inv} y={cy - HANDLE * inv} width={HANDLE * 2 * inv} height={HANDLE * 2 * inv}
            fill="#ffffff" stroke="#2563eb" strokeWidth={1.4 * inv} rx={2 * inv} pointerEvents="none" />
        </g>
      );
    }
    if (o.type === 'text') {
      const b = textBoxNorm(o, W, H);
      const cx = b.maxX * W, cy = b.maxY * H;
      return (
        <g style={{ cursor: 'nwse-resize' }} onPointerDown={(e) => startHandleDrag(e, o, { type: 'text' })}>
          <rect x={cx - TOUCH * inv} y={cy - TOUCH * inv} width={TOUCH * 2 * inv} height={TOUCH * 2 * inv} fill="transparent" />
          <rect x={cx - HANDLE * inv} y={cy - HANDLE * inv} width={HANDLE * 2 * inv} height={HANDLE * 2 * inv}
            fill="#ffffff" stroke="#2563eb" strokeWidth={1.4 * inv} rx={2 * inv} pointerEvents="none" />
        </g>
      );
    }
    return o.points.map((_, i) => vertexHandle(o, i)); // line / polyline / polygon / measure
  };

  // Live rubber-band for the multi draft (segment/label to the hovering cursor).
  const renderDraft = () => {
    if (!draft) return null;
    if (draft._mode === 'two' || draft._mode === 'free') {
      return renderShape({ ...draft, style: draft.calibrate
        ? { stroke: '#1d4ed8', strokeWidth: 2 }
        : styleForNew(draft.type) }, 'draft', true);
    }
    const provisional = hoverPt ? [...draft.points, hoverPt] : draft.points;
    // The draft looks like the mark it will become: the model's default style
    // under the one the new mark is given (a typed area in its type's colour).
    const o = {
      type: draft.type, measureKind: draft.measureKind, points: provisional,
      style: { ...defaultStyle(draft.type), ...styleForNew(draft.type) },
    };
    const pts = toPixels(provisional, W, H);
    return <g>
      {renderShape(o, 'draft', true)}
      {vertexDots(toPixels(draft.points, W, H), o.style.stroke || '#e8650a')}
      {draft.type === 'measure' && pts.length >= (draft.measureKind === 'area' ? 3 : 2) && (
        measureLabelFor({ ...o }, pts)
      )}
    </g>;
  };

  const renderMarquee = () => {
    if (!marquee) return null;
    const r = rectFromCorners(marquee.a, marquee.b);
    const x = r.minX * W, y = r.minY * H, w = (r.maxX - r.minX) * W, h = (r.maxY - r.minY) * H;
    return <rect pointerEvents="none" x={x} y={y} width={w} height={h}
      fill="#2563eb" fillOpacity="0.08" stroke="#2563eb" strokeWidth={1.2 * inv}
      strokeDasharray={`${4 * inv} ${3 * inv}`} />;
  };

  // Faint marker of the active calibration reference line.
  const renderCalibrationRef = () => {
    const c = m.calibration;
    if (!c) return null;
    const a = { x: c.refA.x * W, y: c.refA.y * H };
    const b = { x: c.refB.x * W, y: c.refB.y * H };
    return <g pointerEvents="none" opacity="0.5">
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#059669" strokeWidth={1.5 * inv} strokeDasharray={`${3 * inv} ${3 * inv}`} />
      <circle cx={a.x} cy={a.y} r={3 * inv} fill="#059669" />
      <circle cx={b.x} cy={b.y} r={3 * inv} fill="#059669" />
    </g>;
  };

  const selectedObjects = m.objects.filter((o) => selSet.has(String(o.id)));
  const soleSelected = selectedObjects.length === 1 ? selectedObjects[0] : null;

  return (
    <div style={{
      position: 'absolute', left: 0, top: 0, width: W, height: H,
      transformOrigin: '0 0', transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
      // Read-only (markup closed): the marks still draw on the plan, they just
      // don't take input — taps go through to the plan and its pins.
      pointerEvents: (readOnly || m.tool === 'pan') ? 'none' : 'auto',
    }}>
      <svg
        width={W} height={H} viewBox={`0 0 ${W} ${H}`}
        style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', touchAction: 'none', cursor: (m.tool === 'select' && hoverNode) ? 'grab' : cursorForTool(m.tool) }}
        onPointerDown={onPointerDown}
        onPointerMove={onSvgMove}
        onPointerLeave={() => setHoverNode(null)}
        onDoubleClick={onDoubleClick}
        onContextMenu={(e) => e.preventDefault()}
      >
        {readOnly ? null : renderCalibrationRef()}
        {m.objects.map((o) => renderShape(o, o.id, false))}
        {!readOnly && selectedObjects.map((o) => renderSelectionOutline(o, `sel-${o.id}`))}
        {!readOnly && soleSelected && renderHandles(soleSelected)}
        {!readOnly && m.tool === 'select' && !dragging && hoverNode && (() => {
          const o = m.objects.find((x) => String(x.id) === hoverNode.id);
          const p = o && o.points && o.points[hoverNode.index];
          return p ? (
            <circle pointerEvents="none" cx={p.x * W} cy={p.y * H} r={(VHANDLE + 3) * inv}
              fill="none" stroke="#2563eb" strokeWidth={2 * inv} />
          ) : null;
        })()}
        {!readOnly && renderDraft()}
        {!readOnly && renderMarquee()}
      </svg>

      {!readOnly && typePicker && (() => {
        const plan = typePlanFor(typePicker);
        return plan && plan.types.length ? (
          <MarkTypePicker x={typePicker.x} y={typePicker.y} types={plan.types} title={retypeTitle(plan)}
            currentKey={plan.currentKey} canClear={plan.anyTyped}
            onPick={pickMarkType} onClose={() => setTypePicker(null)} />
        ) : null;
      })()}
      {!readOnly && editing && (
        <input
          autoFocus
          value={editing.value}
          onChange={(e) => setEditing((ed) => (ed ? { ...ed, value: e.target.value } : ed))}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') commitText(); else if (e.key === 'Escape') cancelText(); }}
          onBlur={commitText}
          onPointerDown={(e) => e.stopPropagation()}
          placeholder="Type a note…"
          style={{
            position: 'absolute',
            left: editing.anchor.x * W,
            top: editing.anchor.y * H,
            transform: `scale(${inv})`, transformOrigin: '0 0',
            font: '600 15px var(--font-ui)',
            color: m.drawStyle.stroke, background: 'rgba(255,255,255,0.96)',
            border: '1px solid #2563eb', borderRadius: 4, padding: '2px 6px', minWidth: 120, outline: 'none',
          }}
        />
      )}
    </div>
  );
}

// Counter-scaled measurement label: rounded chip + text, constant screen size.
function MLabel({ x, y, lines, color, inv }) {
  const fs = 12 * inv;
  // White on red, dark on yellow: whichever reads better against the chip.
  const ink = readableInk(color);
  const padX = 6 * inv, padY = 4 * inv, lh = fs * 1.25;
  const wChars = Math.max(...lines.map((s) => (s || '').length));
  const w = wChars * fs * 0.62 + padX * 2;
  const h = lines.length * lh + padY * 2;
  const bx = x + 8 * inv, by = y - h - 4 * inv;
  return (
    <g pointerEvents="none">
      <rect x={bx} y={by} width={w} height={h} rx={4 * inv} fill={color} opacity="0.92" />
      {lines.map((s, i) => (
        <text key={i} x={bx + padX} y={by + padY + lh * (i + 1) - lh * 0.28}
          fontSize={fs} fill={ink} style={{ fontFamily: 'var(--font-ui)', fontWeight: 700 }}>{s}</text>
      ))}
    </g>
  );
}
